import React, { useEffect } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import { Paper } from "@material-ui/core";

import { RouteComponentProps } from "@reach/router";
import { injectIntl } from "react-intl";
import { gql, useQuery } from "@apollo/client";
import Loader from "../loader";
import messages from "./PageViewPlayer.intl";
import InfoTab from "./InfoTab";
import NotesTab from "./NotesTab";
import StatsTab from "./StatsTab";
import Breadcrumb from "./Breadcrumb";
import Link from "../link";
import useIsManager from "../../hooks/useIsManager";
import UserAvatar from "../user-avatar";

const QUERY_PLAYER = gql`
  query playerDetails($id: ID!) {
    player(id: $id) {
      id
      name
      surname
      nickName
      number
      position
      dateOfBirth
      isInvited
      avatarUrl
      user {
        id
        email
      }
      scouting
      team {
        id
        name
      }
      notes {
        note
        date
      }
      totalGoals
      totalMatches
      reviews {
        data {
          speed
          vision
          dribbling
          shoot
          physical
          leadership
          defense
        }
        date
      }
    }
    getPlayerHistoricalData(playerId: $id) {
      league {
        name
        startDate
        endDate
      }
      team {
        name
      }
      totalGoals
      yellowCards
      redCards
    }
    currentUser {
      id
      role
    }
  }
`;

enum TabNames {
  information = "information",
  notes = "notes",
  statistics = "statistics",
}

const StyledPaper = styled(Paper)(({ theme }: IStyledArguments) => ({
  boxSizing: "border-box",
  marginBottom: theme.spacing(5),
  display: "flex",
  flexDirection: "column",
  margin: "0 auto",
  maxWidth: 830,
  borderRadius: "18px",
  overflow: "hidden",
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  background: theme.palette.background.paper,
  [theme.breakpoints.down("md")]: {
    borderRadius: "0",
    boxShadow: "none",
    border: "none",
  },
}));

const HeaderWrapper = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(3, 3, 0),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2, 2, 0),
  },
}));

const IdentityRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.75),
}));

const AvatarWrap = styled("div")({
  position: "relative",
  flexShrink: 0,
  "& .MuiAvatar-root": {
    width: 64,
    height: 64,
    fontSize: "1.6rem",
    background: "rgba(14, 124, 134, 0.08)",
    color: "#0A5E66",
  },
});

const NumberBadge = styled("span")(({ theme }: IStyledArguments) => ({
  position: "absolute",
  right: -3,
  bottom: -3,
  width: 22,
  height: 22,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  border: `2px solid ${theme.palette.background.paper}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "0.6rem",
  color: "#ffffff",
}));

const NameBlock = styled("div")({
  flex: 1,
  minWidth: 0,
});

const PlayerName = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 700,
  fontSize: "1.15rem",
  color: theme.palette.text.primary,
}));

const PlayerMeta = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.8rem",
  color: theme.palette.text.secondary,
  marginTop: 2,
}));

const StatusChip = styled("span")<{ $variant: "invited" | "accepted" }>(
  ({ theme, $variant }: IStyledArguments & { $variant: "invited" | "accepted" }) => ({
    display: "inline-block",
    marginTop: 6,
    borderRadius: 8,
    padding: "2px 9px",
    fontSize: "0.68rem",
    fontWeight: 600,
    background: $variant === "accepted" ? theme.palette.extra.successSoft : theme.palette.extra.accentSoft,
    color: $variant === "accepted" ? theme.palette.extra.success : theme.palette.primary.dark,
  })
);

const EditButton = styled(Link)(({ theme }: IStyledArguments) => ({
  width: 34,
  height: 34,
  borderRadius: "50%",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  "& svg": {
    color: theme.palette.primary.dark,
  },
}));

const StyledTabs = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  borderBottom: `1px solid ${theme.palette.extra.borderColor}`,
}));

const TabButton = styled("button")<{ $active?: boolean; disabled?: boolean }>(
  ({ theme, $active }: IStyledArguments & { $active?: boolean }) => ({
    flex: 1,
    background: "transparent",
    border: "none",
    borderBottom: `3px solid ${$active ? theme.palette.primary.main : "transparent"}`,
    padding: theme.spacing(1.75, 0),
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: $active ? 600 : 500,
    fontSize: "0.9rem",
    color: $active ? theme.palette.primary.dark : theme.palette.text.secondary,
    cursor: "pointer",
    appearance: "none",
    "&:disabled": {
      color: "rgba(16, 21, 26, 0.3)",
      cursor: "default",
    },
  })
);

const TabContainer = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  minHeight: "50vh",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const calculateAge = (dateOfBirth: string) => {
  const now = Date.now();
  const difference = now - Number(dateOfBirth);
  return Math.abs(new Date(difference).getUTCFullYear() - 1970);
};

type PagePlayerProps = Required<Pick<RouteComponentProps, "navigate" | "path">>;

const PageViewPlayer = injectIntl<{ id: string } & PagePlayerProps>(
  ({ id, navigate, path, intl }) => {
    const { data, loading, refetch } = useQuery(QUERY_PLAYER, {
      variables: {
        id,
      },
    });

    const handleRefresh = () => {
      refetch();
    };

    const PATH_TO_TAB: { [path: string]: string } = {
      "/players/:id/information": TabNames.information,
      "/players/:id/notes": TabNames.notes,
      "/players/:id/statistics": TabNames.statistics,
    };

    const TAB_TO_PATH: { [tabName: string]: string } = {
      [TabNames.information]: `/players/${id}/information`,
      [TabNames.notes]: `/players/${id}/notes`,
      [TabNames.statistics]: `/players/${id}/statistics`,
    };

    const currentTab = PATH_TO_TAB[path] || TabNames.information;

    const handleChangeTabs = (tab: TabNames) => {
      navigate(TAB_TO_PATH[tab], {
        replace: true,
      });
    };

    useEffect(() => {
      window.scrollTo(0, 0);
    }, [currentTab]);

    return (
      <StyledPaper>
        <Loader errorMessage={intl.formatMessage(messages.error)} loading={loading} data={data}>
          {({ player, getPlayerHistoricalData, currentUser }) => {
            const isManager = useIsManager(currentUser.role);
            return (
              <>
                <HeaderWrapper>
                  <Breadcrumb player={player} />
                  <IdentityRow>
                    <AvatarWrap>
                      <UserAvatar
                        user={{
                          name: player.name,
                          surname: player.surname,
                          avatarUrl: player.avatarUrl,
                        }}
                      />
                      {player.number && <NumberBadge>{player.number}</NumberBadge>}
                    </AvatarWrap>
                    <NameBlock>
                      <PlayerName>
                        {player.name} {player.surname}
                      </PlayerName>
                      <PlayerMeta>
                        {player.team ? player.team.name : intl.formatMessage(messages.information)}
                        {player.dateOfBirth
                          ? ` · ${calculateAge(player.dateOfBirth)} ${intl.formatMessage(messages.years)}`
                          : ""}
                      </PlayerMeta>
                      {player.isInvited && (
                        <StatusChip $variant="invited">{intl.formatMessage(messages.invited)}</StatusChip>
                      )}
                      {player.user && (
                        <StatusChip $variant="accepted">{intl.formatMessage(messages.accepted)}</StatusChip>
                      )}
                    </NameBlock>
                    {isManager && (
                      <EditButton to={`/players/${id}/edit`}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
                        </svg>
                      </EditButton>
                    )}
                  </IdentityRow>

                  <StyledTabs>
                    <TabButton
                      type="button"
                      data-testid="information-tab"
                      $active={currentTab === TabNames.information}
                      onClick={() => handleChangeTabs(TabNames.information)}
                    >
                      {intl.formatMessage(messages.tabInfo)}
                    </TabButton>
                    <TabButton
                      type="button"
                      data-testid="notes-tab"
                      disabled={!isManager}
                      $active={currentTab === TabNames.notes}
                      onClick={() => isManager && handleChangeTabs(TabNames.notes)}
                    >
                      {intl.formatMessage(messages.tabNotes)}
                    </TabButton>
                    <TabButton
                      type="button"
                      data-testid="statistics-tab"
                      disabled={!isManager}
                      $active={currentTab === TabNames.statistics}
                      onClick={() => isManager && handleChangeTabs(TabNames.statistics)}
                    >
                      {intl.formatMessage(messages.tabEvaluations)}
                    </TabButton>
                  </StyledTabs>
                </HeaderWrapper>
                <TabContainer>
                  {currentTab === TabNames.information && (
                    <InfoTab player={player} historicalData={getPlayerHistoricalData} callback={handleRefresh} />
                  )}
                  {currentTab === TabNames.notes && <NotesTab id={id} notes={player.notes} />}
                  {currentTab === TabNames.statistics && <StatsTab reviews={player.reviews} id={id} />}
                </TabContainer>
              </>
            );
          }}
        </Loader>
      </StyledPaper>
    );
  }
);

export default PageViewPlayer;
